import {
  useAppDispatch,
  useAppSelector,
  useQuery,
} from "features/config/hooks";
import { useEffect, useState, useRef } from "react";
import {
  dailyAttendanceTableData,
  selectdailyAttendanceTableData,
} from "../slices/daily-attendance-table-data.slice";
import {
  GetAdminSessionState,
  selectGetAdminSession,
} from "features/admin/presentation/slices/get-admin-session.slice";

import { createQueryParams } from "features/config/helpers";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Popover,
  FormControlLabel,
  Checkbox,
  Divider,
  MenuItem,
  Select,
  FormControl,
  Breadcrumbs,
  Link,
  Pagination,
  Stack,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

interface AttendanceRecord {
  id: number;
  store_code: string;
  attendance_date: string;
  shift: string;
  employee_name: string;
  attendance: string;
  storeName: string;
}

interface AttendanceKeyMap {
  ID: keyof AttendanceRecord;
  Store_Code: keyof AttendanceRecord;
  Date: keyof AttendanceRecord;
  Shift: keyof AttendanceRecord;
  "Employee Name": keyof AttendanceRecord;
  Attendance: keyof AttendanceRecord;
}

interface FilterCategory {
  name: string;
  key: string;
  options: FilterOption[];
  isDateFilter?: boolean;
}

interface FilterOption {
  value: string;
  label: string;
  checked: boolean;
}

interface DateRangeFilter {
  startDate: string;
  endDate: string;
}

export function FormManpowerAttendance() {
  const query = useQuery();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getAdminSessionState = useAppSelector(selectGetAdminSession);

  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const toggleFilterDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
    setFilterAnchorEl(filterAnchorEl ? null : event.currentTarget);
  };

  const dailyAttendanceTableDataState = useAppSelector(
    selectdailyAttendanceTableData
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategories, setFilterCategories] = useState<FilterCategory[]>(
    []
  );
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<DateRangeFilter>({
    startDate: "",
    endDate: "",
  });
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  // Get all store IDs from the admin session
  const userStores =
    getAdminSessionState.data?.admin?.user_details?.stores || [];
  const storeIds = userStores.map((store) => store.store_id);

  useEffect(() => {
    console.log("User's authorized stores:", userStores);
  }, [userStores]);

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  useEffect(() => {
    console.log("Fetching Daily Attendance Data...");
    if (!dailyAttendanceTableDataState?.data) {
      dispatch(dailyAttendanceTableData());
    }
  }, [dailyAttendanceTableDataState.data, dispatch]);

  useEffect(() => {
    console.log("Forcing fetch for attendance...");
    dispatch(dailyAttendanceTableData());
  }, [dispatch]);

  useEffect(() => {
    setSearchTerm("");
    setDateFilter({ startDate: "", endDate: "" });
    initializeFilterCategories();
    setExpandedCategories([
      "attendance_date",
      "attendance",
      "shift",
      "store_code",
    ]);
  }, []);

  const initializeFilterCategories = () => {
    setFilterCategories([
      {
        name: "Date Range",
        key: "attendance_date",
        options: [],
        isDateFilter: true,
      },
      {
        name: "Attendance Types",
        key: "attendance",
        options: [
          { value: "present", label: "Present", checked: false },
          { value: "late", label: "Late", checked: false },
          { value: "undertime", label: "Undertime", checked: false },
          { value: "absent", label: "Absent", checked: false },
          { value: "rest day", label: "Rest Day", checked: false },
          { value: "on leave (VL)", label: "On Leave (VL)", checked: false },
          {
            value: "on leave (VL half)",
            label: "On Leave (VL Half)",
            checked: false,
          },
          { value: "on leave (SL)", label: "On Leave (SL)", checked: false },
          {
            value: "on leave (SL half)",
            label: "On Leave (SL Half)",
            checked: false,
          },
          { value: "on leave (EL)", label: "On Leave (EL)", checked: false },
          {
            value: "on leave (EL half)",
            label: "On Leave (EL Half)",
            checked: false,
          },
          { value: "on leave (SPL)", label: "On Leave (SPL)", checked: false },
          { value: "on leave (ML)", label: "On Leave (ML)", checked: false },
          { value: "on leave (PL)", label: "On Leave (PL)", checked: false },
          { value: "on leave (BL)", label: "On Leave (BL)", checked: false },
          { value: "awol", label: "AWOL", checked: false },
        ],
      },
      {
        name: "Shift",
        key: "shift",
        options: [
          { value: "am", label: "AM", checked: false },
          { value: "pm", label: "PM", checked: false },
          { value: "n/a", label: "N/A", checked: false },
        ],
      },
    ]);
  };

  const toggleCategoryExpansion = (categoryKey: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryKey)
        ? prev.filter((key) => key !== categoryKey)
        : [...prev, categoryKey]
    );
  };

  const handleFilterOptionToggle = (
    categoryIndex: number,
    optionIndex: number
  ) => {
    const updatedCategories = [...filterCategories];
    updatedCategories[categoryIndex].options[optionIndex].checked =
      !updatedCategories[categoryIndex].options[optionIndex].checked;
    setFilterCategories(updatedCategories);
  };

  const handleDateFilterChange = (
    field: keyof DateRangeFilter,
    value: string
  ) => {
    setDateFilter((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortToggle = () => {
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const attendanceHeaders = [
    "#",
    "Store Name",
    "Date",
    "Shift",
    "Employee Name",
    "Attendance",
    "Actions",
  ];

  const attendanceKeyMap: AttendanceKeyMap = {
    ID: "id",
    Store_Code: "store_code",
    Date: "attendance_date",
    Shift: "shift",
    "Employee Name": "employee_name",
    Attendance: "attendance",
  };

  const getAttendanceChipColor = (attendance: string) => {
    attendance = attendance.toLowerCase();
    if (attendance === "present") {
      return {
        bgcolor: "rgba(46, 125, 50, 0.1)",
        color: "rgba(46, 125, 50, 1)",
      };
    } else if (attendance === "absent" || attendance === "awol") {
      return {
        bgcolor: "rgba(211, 47, 47, 0.1)",
        color: "rgba(211, 47, 47, 1)",
      };
    } else if (attendance === "late" || attendance === "undertime") {
      return {
        bgcolor: "rgba(237, 108, 2, 0.1)",
        color: "rgba(237, 108, 2, 1)",
      };
    } else if (attendance.includes("on leave")) {
      return {
        bgcolor: "rgba(25, 118, 210, 0.1)",
        color: "rgba(25, 118, 210, 1)",
      };
    } else if (attendance === "rest day") {
      return {
        bgcolor: "rgba(156, 39, 176, 0.1)",
        color: "rgba(156, 39, 176, 1)",
      };
    } else {
      return {
        bgcolor: "rgba(117, 117, 117, 0.1)",
        color: "rgba(117, 117, 117, 1)",
      };
    }
  };

  const isDateInRange = (
    dateStr: string,
    startDate: string,
    endDate: string
  ): boolean => {
    if (!startDate && !endDate) return true;
    const date = new Date(dateStr);
    if (startDate && !endDate) {
      const start = new Date(startDate);
      return date >= start;
    }
    if (!startDate && endDate) {
      const end = new Date(endDate);
      return date <= end;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    return date >= start && date <= end;
  };

  const getFormData = () => {
    const data = dailyAttendanceTableDataState.data?.attendanceData || [];
    const activeFilters = filterCategories.reduce((acc, category) => {
      if (!category.isDateFilter) {
        const selectedOptions = category.options
          .filter((option) => option.checked)
          .map((option) => option.value);
        if (selectedOptions.length > 0) {
          acc[category.key] = selectedOptions;
        }
      }
      return acc;
    }, {} as Record<string, string[]>);

    let filtered = data.filter((item: any) => {
      // Filter by matching storeName with user's authorized stores
      if (storeIds.length > 0) {
        const isAuthorizedStore = userStores.some(
          (store) =>
            store.name === item.storeName ||
            String(store.store_id) === String(item.storeName)
        );
        console.log(
          `Checking store: ${item.storeName}, Authorized: ${isAuthorizedStore}`
        );
        if (!isAuthorizedStore) {
          return false;
        }
      }

      if (
        (dateFilter.startDate || dateFilter.endDate) &&
        !isDateInRange(
          item["attendance_date"],
          dateFilter.startDate,
          dateFilter.endDate
        )
      ) {
        return false;
      }

      for (const [key, values] of Object.entries(activeFilters)) {
        if (values.length > 0) {
          const itemValue = String(item[key] || "").toLowerCase();
          if (
            !values.some((value) => itemValue.includes(value.toLowerCase()))
          ) {
            return false;
          }
        }
      }

      if (searchTerm && searchTerm.trim() !== "") {
        return Object.entries(item).some(([key, value]) => {
          if (key === "id") return false;
          return String(value).toLowerCase().includes(searchTerm.toLowerCase());
        });
      }

      return true;
    });

    // Apply sorting by date
    filtered.sort((a, b) => {
      const dateA = new Date(a.attendance_date);
      const dateB = new Date(b.attendance_date);
      return sortDirection === "desc"
        ? dateB.getTime() - dateA.getTime()
        : dateA.getTime() - dateB.getTime();
    });

    return filtered;
  };

  const getActiveFilterCount = () => {
    const standardFilters = filterCategories.reduce((count, category) => {
      if (!category.isDateFilter) {
        return (
          count + category.options.filter((option) => option.checked).length
        );
      }
      return count;
    }, 0);
    const dateFilterActive = dateFilter.startDate || dateFilter.endDate ? 1 : 0;
    return standardFilters + dateFilterActive;
  };

  const filteredData = getFormData();
  const maxPage = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const displayData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );
  console.log("Display Data:", displayData);

  const handleEditClick = (id: number) => {
    console.log(`Edit clicked for record ID: ${id}`);
  };

  return (
    <Box sx={{ width: "100%", bgcolor: "#FFFFFF" }}>
      <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          <Link
            underline="hover"
            sx={{ display: "flex", alignItems: "center" }}
            color="inherit"
            href="/admin/ms6/dashboard"
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Manpower Management</Typography>
        </Breadcrumbs>
      </Box>

      <Typography
        variant="h5"
        component="h1"
        sx={{ p: 2, fontFamily: "'Bebas Neue', sans-serif", fontSize: "2rem" }}
      >
        ATTENDANCE RECORDS
      </Typography>

      <Box sx={{ display: "flex", p: 2, gap: 2, flexWrap: "wrap" }}>
        <TextField
          placeholder="Search"
          variant="outlined"
          size="small"
          InputProps={{
            startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
          }}
          sx={{ flexGrow: 1 }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={toggleFilterDialog}
          ref={filterButtonRef}
          size="small"
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "120px",
            justifyContent: "space-between",
          }}
        >
          {getActiveFilterCount() === 0
            ? "Filter"
            : `Filters (${getActiveFilterCount()})`}
        </Button>

        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            displayEmpty
          >
            <MenuItem value={25}>25 rows</MenuItem>
            <MenuItem value={50}>50 rows</MenuItem>
            <MenuItem value={100}>100 rows</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box
        sx={{
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          m: 2,
        }}
      >
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#222222" }}>
                {attendanceHeaders.map((header) => (
                  <TableCell
                    key={header}
                    sx={{
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      borderBottom: "none",
                      cursor: header === "Date" ? "pointer" : "default",
                    }}
                    onClick={header === "Date" ? handleSortToggle : undefined}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {header}
                      {header === "Date" &&
                        (sortDirection === "desc" ? (
                          <ArrowDownwardIcon
                            sx={{ ml: 1, fontSize: 16, color: "#FFFFFF" }}
                          />
                        ) : (
                          <ArrowUpwardIcon
                            sx={{ ml: 1, fontSize: 16, color: "#FFFFFF" }}
                          />
                        ))}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {displayData.length > 0 ? (
                displayData.map((item: any, index: number) => (
                  <TableRow
                    key={item.id || index}
                    sx={{
                      "&:hover": { bgcolor: "#f5f5f5" },
                    }}
                  >
                    <TableCell sx={{ borderBottom: "none" }}>
                      {(page - 1) * rowsPerPage + index + 1}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      {item.storeName}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      {item.attendance_date}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      {item.shift}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      {item.employee_name}
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Chip
                        label={item.attendance}
                        size="small"
                        sx={getAttendanceChipColor(item.attendance)}
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(item.id)}
                        sx={{
                          borderRadius: "16px",
                          fontSize: "0.75rem",
                          textTransform: "none",
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={attendanceHeaders.length}
                    align="center"
                    sx={{ py: 4, borderBottom: "none" }}
                  >
                    {searchTerm || getActiveFilterCount() > 0
                      ? "No matching records found. Try adjusting your filters."
                      : "No records found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {displayData.length} of {filteredData.length} records
          {(searchTerm || getActiveFilterCount() > 0) &&
            ` (filtered from ${
              dailyAttendanceTableDataState.data?.attendanceData?.length || 0
            } total)`}
        </Typography>

        <Pagination
          count={maxPage}
          page={page}
          onChange={handleChangePage}
          siblingCount={1}
          boundaryCount={1}
        />
      </Box>

      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 500 },
        }}
      >
        <Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0" }}>
          <Typography variant="subtitle1" fontWeight="medium">
            Filter Attendance Records
          </Typography>
          {getActiveFilterCount() > 0 && (
            <Typography variant="caption" color="text.secondary">
              {getActiveFilterCount()}{" "}
              {getActiveFilterCount() === 1 ? "filter" : "filters"} applied
            </Typography>
          )}
        </Box>
        <Box sx={{ maxHeight: 350, overflow: "auto" }}>
          {filterCategories.map((category, categoryIndex) => (
            <Box key={categoryIndex} sx={{ borderBottom: "1px solid #f0f0f0" }}>
              <Box
                sx={{
                  p: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  cursor: "pointer",
                }}
                onClick={() => toggleCategoryExpansion(category.key)}
              >
                <Typography variant="subtitle2">{category.name}</Typography>
                <KeyboardArrowDownIcon
                  sx={{
                    transform: expandedCategories.includes(category.key)
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </Box>
              {expandedCategories.includes(category.key) && (
                <Box sx={{ p: 2, pt: 0, pl: 4, bgcolor: "#f5f5f5" }}>
                  {category.isDateFilter ? (
                    <Stack spacing={2}>
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          Start Date
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateFilter.startDate}
                          onChange={(e) =>
                            handleDateFilterChange("startDate", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          display="block"
                          gutterBottom
                        >
                          End Date
                        </Typography>
                        <TextField
                          type="date"
                          size="small"
                          fullWidth
                          value={dateFilter.endDate}
                          onChange={(e) =>
                            handleDateFilterChange("endDate", e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                        />
                      </Box>
                    </Stack>
                  ) : (
                    category.options.map((option, optionIndex) => (
                      <FormControlLabel
                        key={optionIndex}
                        control={
                          <Checkbox
                            checked={option.checked}
                            onChange={() =>
                              handleFilterOptionToggle(
                                categoryIndex,
                                optionIndex
                              )
                            }
                            size="small"
                          />
                        }
                        label={
                          <Typography variant="body2">
                            {option.label}
                          </Typography>
                        }
                      />
                    ))
                  )}
                </Box>
              )}
            </Box>
          ))}
        </Box>
        <Box
          sx={{
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button
            onClick={() => {
              const resetCategories = filterCategories.map((category) => ({
                ...category,
                options: category.options.map((option) => ({
                  ...option,
                  checked: false,
                })),
              }));
              setFilterCategories(resetCategories);
              setDateFilter({ startDate: "", endDate: "" });
            }}
            color="error"
            size="small"
          >
            Clear all
          </Button>
          <Button
            onClick={() => setFilterAnchorEl(null)}
            variant="contained"
            size="small"
          >
            Apply
          </Button>
        </Box>
      </Popover>
    </Box>
  );
}
